import { useState, useRef } from 'react';
import styles from './useToaster.module.css';
import { useInterval } from './useInterval';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';

export interface IToastRequest {
  message: string;
  autoHideDelay?: number | null;
  style?: ToastStyle;
}

export interface IToastItem extends IToastRequest {
  index: number;
  timestamp: Date;
  visibility: ToastVisibility;
}

export enum ToastVisibility {
  VISIBLE = 1,
  HIDING = 2
}

export enum ToastStyle {
  DEFAULT = 0,
  SUCCESS = 1,
  WARNING = 2,
  ERROR = 3
}

export const useToaster = () => {
  const toastCounter = useRef(0);
  const toastAutoHideTracker = useRef<number[]>([]);
  const [toasts, setToasts] = useState<IToastItem[]>([]);

  const addToast = (
    message: string,
    style: ToastStyle = ToastStyle.DEFAULT,
    autoHideDelay = 3000
  ) => {
    addToastItem({
      message,
      style,
      autoHideDelay
    });
  };

  const addToastItem = (toast: IToastRequest) => {
    toastCounter.current++;
    const toastItem: IToastItem = {
      message: toast.message,
      index: toastCounter.current,
      timestamp: new Date(),
      autoHideDelay: toast.autoHideDelay ?? null,
      style: toast.style ?? ToastStyle.DEFAULT,
      visibility: ToastVisibility.VISIBLE
    };
    setToasts((t) => {
      const toasts = [...t];
      toasts.push(toastItem);
      return toasts;
    });
  };

  const removeToast = (toastIndex: number) => {
    setToasts((t) => {
      let toasts = [...t];
      toasts = toasts.filter((x) => x.index !== toastIndex);
      return toasts;
    });
  };

  const hideToast = (toastIndex: number) => {
    setToasts((t) => {
      const toasts = [...t];
      const hideToast = toasts.find((x) => x.index === toastIndex);
      if (hideToast) {
        hideToast.visibility = ToastVisibility.HIDING;
        hideToast.autoHideDelay = null;
      }
      return toasts;
    });
    setTimeout(() => removeToast(toastIndex), 600);
  };

  useInterval(
    () => {
      const currentTime = new Date().getTime();
      for (const toast of toasts.filter(
        (x) =>
          !toastAutoHideTracker.current.includes(x.index) &&
          x.visibility === ToastVisibility.VISIBLE &&
          x.autoHideDelay &&
          currentTime - x.timestamp.getTime() > x.autoHideDelay
      )) {
        toastAutoHideTracker.current.push(toast.index);
        hideToast(toast.index);
      }
    },
    toasts.length > 0 ? 100 : null
  );

  const ToastContainer = () => {
    return (
      <div className={styles.toastContainer}>
        {toasts.map((t) => {
          const toastVisStyle =
            t.visibility === ToastVisibility.HIDING ? styles.hidingToast : styles.toast;

          let toastColorStyle = styles.defaultToast;
          switch (t.style) {
            case ToastStyle.SUCCESS:
              toastColorStyle = styles.successToast;
              break;
            case ToastStyle.ERROR:
              toastColorStyle = styles.errorToast;
              break;
            case ToastStyle.WARNING:
              toastColorStyle = styles.warningToast;
              break;
          }

          return (
            <div key={t.index} className={`${toastVisStyle} ${toastColorStyle}`}>
              <div className={styles.message}>{t.message}</div>
              <div>
                <a
                  href="#"
                  className={styles.toastCloseIcon}
                  onClick={(e) => {
                    e.preventDefault();
                    hideToast(t.index);
                  }}>
                  <FontAwesomeIcon icon={faTimes} />
                </a>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return {
    addToast,
    ToastContainer
  };
};
